/**
 * File-type icon by filename — one mapping for Drive, the Knowledge Area
 * document list, the command palette and chat sources, so a photo reads as a
 * photo wherever it is listed (cdb_tqs#1515).
 *
 * Display only. Which app or renderer opens a file is decided by
 * `classifyFile` (#1611), never by the icon.
 */
import {
  File as FileIcon,
  FileAudio,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Presentation,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { getFileTypeFromFilename, type DriveFileType } from '@/types/cdb-tqs-documents'

export const typeToIcon: Record<DriveFileType, LucideIcon> = {
  pdf: FileText,
  audio: FileAudio,
  video: FileVideo,
  image: FileImage,
  text: FileText,
  spreadsheet: FileSpreadsheet,
  presentation: Presentation,
  other: FileIcon,
}

/** Image extensions that get the image icon. heic/heif are included although
 *  no preview here renders them: the name alone tells the user it is a photo. */
const IMAGE_ICON_EXTENSIONS = new Set([
  'jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'svg', 'tiff', 'heic', 'heif',
])

/** True when the filename ends in an image extension. A bare word such as
 *  "jpg" without a dot is not a filename and does not count. */
export function isImageFileName(name: string | null | undefined): boolean {
  const match = /\.([^./\\\s]+)$/.exec(name?.trim() ?? '')
  return match !== null && IMAGE_ICON_EXTENSIONS.has(match[1].toLowerCase())
}

/** Icon for a file, chosen by its extension. */
export function fileIconFor(name: string | null | undefined): LucideIcon {
  if (!name) return FileIcon
  if (isImageFileName(name)) return FileImage
  return typeToIcon[getFileTypeFromFilename(name)]
}

/** Video formats a Knowledge Area ingests and a browser can usually play
 *  (video ingest plan §9). Other containers (mkv, avi, …) stay plain files. */
export const VIDEO_DOCUMENT_EXTENSIONS = new Set(['mp4', 'm4v', 'mov', 'webm'])

/** True when the filename ends in one of the ingestable video extensions. */
export function isVideoFileName(name: string | null | undefined): boolean {
  const match = /\.([^./\\\s]+)$/.exec(name?.trim() ?? '')
  return match !== null && VIDEO_DOCUMENT_EXTENSIONS.has(match[1].toLowerCase())
}

/**
 * Whether a Knowledge Area document is a video: the backend's `media_type`
 * first (video plan TQS-1), then `mime_type`, then the extension.
 */
export function isVideoDocument(doc: {
  mediaType?: string | null
  mimeType?: string | null
  filename?: string | null
}): boolean {
  if (doc.mediaType === 'video') return true
  if (doc.mediaType) return false
  if (doc.mimeType) return doc.mimeType.toLowerCase().startsWith('video/')
  return isVideoFileName(doc.filename)
}

/** The formats the Brain renders thumbnails for (cdb_tqs#1515). */
const THUMBNAIL_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp'])

/**
 * Whether a document is an image, preferring what the backend says
 * (`media_type`, then `mime_type`) over the extension. Older backends send
 * neither, so the filename stays the fallback.
 */
export function isImageDocument(doc: {
  mediaType?: string | null
  mimeType?: string | null
  filename?: string | null
}): boolean {
  if (doc.mediaType === 'image') return true
  if (doc.mediaType === 'document') return false
  if (doc.mimeType) return doc.mimeType.toLowerCase().startsWith('image/')
  return isImageFileName(doc.filename)
}

/**
 * Whether asking for a thumbnail makes sense. The backend decides for an image
 * or video document; without its word only the formats it renders are asked for, so a
 * `.gif` or `.heic` does not cost a request that ends in 404.
 */
export function canHaveThumbnail(doc: {
  mediaType?: string | null
  mimeType?: string | null
  filename?: string | null
}): boolean {
  // A processed video has a poster (video plan TQS-4). Only the backend's word
  // counts: an older backend answers 404 for every video row.
  if (doc.mediaType) return doc.mediaType === 'image' || doc.mediaType === 'video'
  if (doc.mimeType) return /^image\/(jpeg|png|webp)$/i.test(doc.mimeType)
  const match = /\.([^./\\\s]+)$/.exec(doc.filename?.trim() ?? '')
  return match !== null && THUMBNAIL_EXTENSIONS.has(match[1].toLowerCase())
}
